import { useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Loader } from '@hivebrite/design-system/Loader';
import { convertRemToPx } from '@hivebrite/design-system/Theme';

import WidthContentBox from 'hb-builder/components/BuilderComponents/common/Section/components/WidthContentBox';
import { sectionImageRecommendedSize } from 'hb-builder/components/BuilderComponents/constants/imageSizes';
import ImageSetter from 'hb-builder/components/common/ImageSetter';
import type { RenderProps } from 'hb-builder/components/common/type/RenderProps';
import {
  useComponentContext,
  useBuilderConfigContext,
  useRendererContext,
  useSectionContext,
} from 'hb-builder/hooks';
import type { BuilderComponent } from 'hb-builder/types';
import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';
import type { HeroItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/heroItemAttributes';
import Grid, { Col, Row } from 'hb-react/shared/components/DesignSystem/Grid';

import { DEFAULT_BACKGROUND, VERTICAL_ALIGN_MAPPER } from './constants';

const slideVerticalAlignment = (
  direction: keyof typeof VERTICAL_ALIGN_MAPPER = 'middle',
) => VERTICAL_ALIGN_MAPPER[direction];

const getHasButton = (item) =>
  !!item.children?.find(
    (child) => child.type === 'Button' && !child?.attributes?.hidden,
  );

const Slide: BuilderComponent = () => <div />; // never used

const RenderBuilder = ({ children }) => {
  const {
    features: { workspace: workspaceConfig },
  } = useBuilderConfigContext();
  const { item } = useComponentContext();
  const hasButton = getHasButton(item);
  const {
    attributes: sectionAttributes,
    bodyWidth,
    sectionWidth,
  } = useSectionContext();

  return (
    <SlideWrapper
      $attributes={(item as DocumentItem<HeroItemAttributes>).attributes}
      minWidth={sectionWidth}
    >
      {item?.attributes?.isLoadingSrc ? (
        <Box>
          <Loader size={40} />
        </Box>
      ) : (
        <>
          <WidthContentBox
            height={!hasButton ? 'auto' : undefined}
            width={sectionAttributes?.outsideGrid ? bodyWidth : undefined}
            workspaceConfig={workspaceConfig}
          >
            <Grid
              columnsInMobile
              mx={0}
              numCols={{
                _: 6,
                m: 12,
                l: 12,
                xl: 12,
              }}
              width="100%"
              mt={0}
            >
              <Row justifyCenter>
                <Col overrideRowGap={0}>{children}</Col>
              </Row>
            </Grid>
          </WidthContentBox>
          <ImageSetter
            attributeName="containerBackgroundImage"
            item={item}
            imageSize={sectionImageRecommendedSize}
          />
        </>
      )}
    </SlideWrapper>
  );
};

const Render = ({
  children,
  item,
}: RenderProps<DocumentItem<HeroItemAttributes>>) => {
  // @ts-expect-error TODO: isActive is not set in the context anymore, should we keep it?
  const { bodyWidth, isActive, workspaceConfig } = useRendererContext();
  const slideRef = useRef<HTMLDivElement>();
  const hasButton = getHasButton(item);
  const {
    calculateSectionHeight,
    sectionWidth,
    attributes: sectionAttributes,
  } = useSectionContext();

  const toggleButtonTabNavigation = (tabIndex) => {
    const node = slideRef.current;

    if (node) {
      const buttonWrapperNode = node.lastChild;

      if (buttonWrapperNode?.childNodes) {
        const buttonNode = buttonWrapperNode.childNodes[0];

        buttonNode &&
          (buttonNode as HTMLElement).setAttribute('tabindex', tabIndex);
      }
    }
  };

  useEffect(() => {
    calculateSectionHeight();
  }, [calculateSectionHeight]);

  useEffect(() => {
    // As we can't give props to children components,
    // we have to dig in the Slide Reference to change focusability
    toggleButtonTabNavigation(isActive ? '0' : '-1');
  }, [slideRef.current, isActive]);

  return (
    <SlideWrapper
      $attributes={item?.attributes}
      minWidth={sectionWidth}
      aria-label={item?.attributes?.containerBackgroundImageAlt}
    >
      <WidthContentBox
        height={!hasButton ? 'auto' : undefined}
        width={sectionAttributes?.outsideGrid ? bodyWidth : undefined}
        workspaceConfig={workspaceConfig}
      >
        <Grid
          columnsInMobile
          mx={0}
          numCols={{
            _: 6,
            m: 12,
            l: 12,
            xl: 12,
          }}
          width="100%"
          mt={0}
        >
          <Row justifyCenter>
            <Col>{children}</Col>
          </Row>
        </Grid>
      </WidthContentBox>
    </SlideWrapper>
  );
};

Slide.canBeDoubleClicked = true;
Slide.canBeDragged = false;
Slide.canBeListed = false;
Slide.canBeSelected = false;
Slide.componentName = 'Slide';

const defaultConfig = ({
  bodyWidth,
  components,
  featureFlags,
  features,
  theme,
  index,
}) => {
  const { Button, Text } = components;

  const headline = Text.item({
    features: {
      ...features,
      text: {
        ...features?.text,
        containerPadding: 0,
        content: __('Headline goes here'),
        defaultTextColor: true,
        horizontalAlign: 'center',
        textColor: '#fff',
        textFontSize: convertRemToPx(theme.fontSizes[10]),
        tied: true,
      },
    },
    theme,
  });

  const subline = Text.item({
    features: {
      ...features,
      text: {
        ...features?.text,
        containerPadding: 0,
        content: __(
          'Subline goes here, you can write as much as you want here.',
        ),
        defaultTextColor: true,
        horizontalAlign: 'center',
        textColor: '#fff',
        textFontSize: convertRemToPx(theme.fontSizes[2]),
        tied: true,
      },
    },
    theme,
  });

  const button = Button.item({
    components,
    featureFlags,
    features: {
      ...features,
      button: {
        ...features?.button,
        containerPadding: '32px 0px 64px 0px',
        horizontalAlign: 'center',
        tied: true,
      },
    },
    theme,
  });

  return {
    children: [headline, subline, button],
    containerBackgroundImage: DEFAULT_BACKGROUND(index).IMAGE_URL,
    containerBackgroundImageAlt: DEFAULT_BACKGROUND(index).IMAGE_ALT,
    containerBackgroundPosition: DEFAULT_BACKGROUND(index).IMAGE_POSITION,
    containerBackgroundRepeat: DEFAULT_BACKGROUND(index).IMAGE_REPEAT,
    containerBackgroundSize: DEFAULT_BACKGROUND(index).IMAGE_COVER,
    containerPadding: '20px 20px 20px 20px',
    justifyContent: 'center',
    minWidth: bodyWidth,
    verticalAlign: 'middle',
    type: Slide.componentName,
  };
};

Slide.item = ({
  bodyWidth,
  components,
  featureFlags,
  features,
  theme,
  index,
}) => {
  const slideConfig = {
    ...defaultConfig({
      bodyWidth,
      components,
      featureFlags,
      features,
      theme,
      index,
    }),
    ...(features?.heroSlider?.slider || {}),
  };

  return {
    attributes: {
      containerBackgroundImage: slideConfig.containerBackgroundImage,
      containerBackgroundImageAlt: slideConfig.containerBackgroundImageAlt,
      containerBackgroundPosition: slideConfig.containerBackgroundPosition,
      containerBackgroundRepeat: slideConfig.containerBackgroundRepeat,
      containerBackgroundSize: slideConfig.containerBackgroundSize,
      containerPadding: slideConfig.containerPadding,
      minWidth: slideConfig.bodyWidth,
      justifyContent: slideConfig.justifyContent,
      verticalAlign: slideConfig.verticalAlign,
    },
    children: slideConfig.children,
    type: Slide.componentName,
  };
};

Slide.RenderBuilder = RenderBuilder;
Slide.Render = Render;

export default Slide;

interface SlideWrapperProps {
  $attributes?: HeroItemAttributes;
}

const SlideWrapper = styled(Box).attrs(
  ({ $attributes }: SlideWrapperProps) => ({
    flexDirection: 'column',
    height: 'auto',
    justifyContent: slideVerticalAlignment($attributes?.verticalAlign),
    padding: $attributes?.containerPadding,
  }),
)<SlideWrapperProps>`
  ${({ $attributes }) =>
    css`
      background-color: ${$attributes?.containerBackgroundColor};
      background-image: ${$attributes?._imageData ||
      $attributes?.containerBackgroundImage
        ? `url(${
            $attributes?._imageData || $attributes.containerBackgroundImage
          })`
        : 'none'};
      background-position: ${$attributes?.containerBackgroundPosition};
      background-repeat: ${$attributes?.containerBackgroundRepeat};
      background-size: ${$attributes?.containerBackgroundSize};
    `}
`;
